import { ContentId } from 'src/utils/constants/contentId';

/**
 * A single data point in a preset curve.
 * `daysToEvent` is the number of days before the event (0 = event day, 30 = 30 days out).
 * `value` is the normalized value:
 *   - For price curves: ratio relative to price at the seller's current days-to-event (1.0 = same price)
 *   - For sales timing curves: cumulative proportion of sales by that point (0.0 to 1.0)
 */
export interface PresetDataPoint {
  daysToEvent: number;
  value: number;
}

/**
 * The 5 percentiles used in the strategy calculator UI.
 * Maps to the Figma presets: Weak / Below Avg / Typical / Above Avg / Hot.
 */
export type PresetPercentile = 'p10' | 'p25' | 'p50' | 'p75' | 'p90';

export const PRESET_PERCENTILES: PresetPercentile[] = [
  'p10',
  'p25',
  'p50',
  'p75',
  'p90',
];

/**
 * A complete preset curve at one percentile level.
 */
export interface PresetCurve {
  percentile: PresetPercentile;
  label: string;
  data: PresetDataPoint[];
}

/**
 * All preset curves for a single category segment.
 */
export interface CategoryPresets {
  segment: string;
  priceTrend: PresetCurve[];
  salesTiming: PresetCurve[];
}

/**
 * The top-level structure of the preset data files.
 */
export type PresetData = CategoryPresets[];

/** ContentId mappings for price trend curve labels — resolve via useContent() */
export const PRICE_PRESET_CONTENT_IDS: Record<PresetPercentile, ContentId> = {
  p90: ContentId.StrategyCalculatorPriceTrendHot,
  p75: ContentId.StrategyCalculatorPriceTrendAboveAvg,
  p50: ContentId.StrategyCalculatorPriceTrendTypical,
  p25: ContentId.StrategyCalculatorPriceTrendBelowAvg,
  p10: ContentId.StrategyCalculatorPriceTrendWeak,
};

/** ContentId mappings for sales timing curve labels — resolve via useContent() */
export const SALES_TIMING_PRESET_CONTENT_IDS: Record<
  PresetPercentile,
  ContentId
> = {
  p90: ContentId.StrategyCalculatorSalesTimingHot,
  p75: ContentId.StrategyCalculatorSalesTimingAboveAvg,
  p50: ContentId.StrategyCalculatorSalesTimingTypical,
  p25: ContentId.StrategyCalculatorSalesTimingBelowAvg,
  p10: ContentId.StrategyCalculatorSalesTimingWeak,
};
